import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { platformSalesProductApi } from '../../api/platformSalesProduct';
import { message } from 'antd';

// 获取平台代售商品列表
export const fetchPlatformSalesProductList = createAsyncThunk(
  'platformSalesProduct/fetchList',
  async (params, { rejectWithValue }) => {
    try {
      const response = await platformSalesProductApi.getPlatformSalesProductList(params);
      // 后端返回的是 IPage 格式，包含 records, total, current, size 等字段
      const data = response.data || response;
      return {
        list: data.records || data.list || [],
        total: data.total || 0,
        current: data.current || params.page || 1,
        pageSize: data.size || params.size || 10
      };
    } catch (error) {
      console.error('获取平台代售商品列表失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取平台代售商品列表失败' });
    }
  }
);

// 获取平台代售商品详情
export const fetchPlatformSalesProductDetail = createAsyncThunk(
  'platformSalesProduct/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await platformSalesProductApi.getPlatformSalesProductDetail(id);
      return response.data || response;
    } catch (error) {
      console.error('获取平台代售商品详情失败:', error);
      return rejectWithValue(error.response?.data || { message: '获取平台代售商品详情失败' });
    }
  }
);

// 创建平台代售商品
export const createPlatformSalesProduct = createAsyncThunk(
  'platformSalesProduct/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await platformSalesProductApi.createPlatformSalesProduct(data);
      message.success('创建商品成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '创建商品失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 更新平台代售商品
export const updatePlatformSalesProduct = createAsyncThunk(
  'platformSalesProduct/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await platformSalesProductApi.updatePlatformSalesProduct(id, data);
      message.success('更新商品成功');
      return response.data || response;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '更新商品失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 删除平台代售商品
export const deletePlatformSalesProduct = createAsyncThunk(
  'platformSalesProduct/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await platformSalesProductApi.deletePlatformSalesProduct(id);
      message.success('删除商品成功');
      return id;
    } catch (error) {
      const errorMsg = error.response?.data?.message || '删除商品失败';
      message.error(errorMsg);
      return rejectWithValue(error.response?.data || { message: errorMsg });
    }
  }
);

// 根据订单ID查询商品列表
export const fetchProductsByOrderId = createAsyncThunk(
  'platformSalesProduct/fetchByOrderId',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await platformSalesProductApi.getProductsByOrderId(orderId);
      return response.data || response;
    } catch (error) {
      console.error('根据订单ID查询商品列表失败:', error);
      return rejectWithValue(error.response?.data || { message: '根据订单ID查询商品列表失败' });
    }
  }
);

// 平台代售商品 Slice
const platformSalesProductSlice = createSlice({
  name: 'platformSalesProduct',
  initialState: {
    list: [],
    currentProduct: null,
    productsByOrder: [],
    loading: false,
    error: null,
    pagination: {
      total: 0,
      current: 1,
      pageSize: 10
    }
  },
  reducers: {
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    clearProductsByOrder: (state) => {
      state.productsByOrder = [];
    }
  },
  extraReducers: (builder) => {
    builder
      // 获取列表
      .addCase(fetchPlatformSalesProductList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPlatformSalesProductList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.list;
        state.pagination.total = action.payload.total;
        state.pagination.current = action.payload.current;
        state.pagination.pageSize = action.payload.pageSize;
      })
      .addCase(fetchPlatformSalesProductList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // 获取详情
      .addCase(fetchPlatformSalesProductDetail.fulfilled, (state, action) => {
        state.currentProduct = action.payload;
      })
      
      // 创建商品
      .addCase(createPlatformSalesProduct.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
        state.pagination.total += 1;
      })
      
      // 更新商品
      .addCase(updatePlatformSalesProduct.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentProduct && state.currentProduct.id === action.payload.id) {
          state.currentProduct = action.payload;
        }
      })
      
      // 删除商品
      .addCase(deletePlatformSalesProduct.fulfilled, (state, action) => {
        state.list = state.list.filter(item => item.id !== action.payload);
        state.pagination.total -= 1;
        if (state.currentProduct && state.currentProduct.id === action.payload) {
          state.currentProduct = null;
        }
      })
      
      // 根据订单ID查询商品列表
      .addCase(fetchProductsByOrderId.fulfilled, (state, action) => {
        state.productsByOrder = Array.isArray(action.payload) ? action.payload : [];
      });
  }
});

export const { setPagination, clearCurrentProduct, clearProductsByOrder } = platformSalesProductSlice.actions;
export default platformSalesProductSlice.reducer;

